import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography,
  Box,
  Grid,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import { 
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Star as StarIcon
} from '@mui/icons-material';

const ViewUserModal = ({ open, onClose, user }) => {
  if (!user) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin': return 'error';
      case 'moderator': return 'primary';
      case 'user': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'blocked': 
      case 'suspended': return 'warning';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>User Details</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar 
            src={user.profile?.currentAvatar?.url || user.getAvatar?.()} 
            alt={user.username}
            sx={{ width: 80, height: 80 }}
          />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {user.profile?.displayName || user.username}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              @{user.username}
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Chip 
                label={user.role || 'User'} 
                color={getRoleColor(user.role)} 
                size="small" 
              />
              <Chip 
                label={user.status || 'Active'} 
                color={getStatusColor(user.status)} 
                size="small" 
              />
              {user.isPremium && (
                <Chip 
                  icon={<StarIcon />}
                  label="Premium" 
                  color="warning" 
                  size="small" 
                />
              )}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Contact Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon color="action" fontSize="small" />
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body2">{user.email || 'N/A'}</Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color="action" fontSize="small" />
              <Box>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body2">{user.phoneNumber || 'N/A'}</Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Profile Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="action" fontSize="small" />
              <Box>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <Typography variant="body2">
                  {user.profile?.city && user.profile?.country 
                    ? `${user.profile.city}, ${user.profile.country}`
                    : user.profile?.country || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon color="action" fontSize="small" />
              <Box>
                <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                <Typography variant="body2">
                  {formatDate(user.profile?.dateOfBirth)}
                  {user.profile?.age && ` (${user.profile.age} years old)`}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="action" fontSize="small" />
              <Box>
                <Typography variant="caption" color="text.secondary">Pronoun</Typography>
                <Typography variant="body2">{user.profile?.pronoun || 'N/A'}</Typography>
              </Box>
            </Box>
          </Grid>

          {user.adminLevel && (
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">Admin Level</Typography>
                <Typography variant="body2">{user.adminLevel}</Typography>
              </Box>
            </Grid>
          )}

          {user.profile?.bio && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Bio</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {user.profile.bio}
                </Typography>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Account Status
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Email Verified</Typography>
            <Typography variant="body2">
              <Chip 
                label={user.isEmailVerified ? 'Yes' : 'No'} 
                color={user.isEmailVerified ? 'success' : 'default'} 
                size="small" 
              />
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Phone Verified</Typography>
            <Typography variant="body2">
              <Chip 
                label={user.isPhoneVerified ? 'Yes' : 'No'} 
                color={user.isPhoneVerified ? 'success' : 'default'} 
                size="small" 
              />
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Premium Status</Typography>
            <Typography variant="body2">
              <Chip 
                label={user.isPremium ? 'Premium' : 'Free'} 
                color={user.isPremium ? 'warning' : 'default'} 
                size="small" 
              />
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Trust Score</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {typeof user.trustScore === 'number' ? user.trustScore : '—'}
              </Typography>
              {user.trustTierLabel && (
                <Chip
                  label={user.trustTierLabel}
                  size="small"
                  sx={{
                    bgcolor: user.trustTierLabel === 'Trusted' ? '#2e7d32' : user.trustTierLabel === 'Established' ? '#1565C0' : '#9e9e9e',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Created At</Typography>
            <Typography variant="body2">{formatDate(user.createdAt)}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Last Updated</Typography>
            <Typography variant="body2">{formatDate(user.updatedAt)}</Typography>
          </Grid>

          {user.lastOnline && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Last Online</Typography>
              <Typography variant="body2">{formatDate(user.lastOnline)}</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewUserModal;
